import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

import { useFolders } from "src/api/projectFolders/projectFolderService";
import type { ProjectFolder } from "src/api/projectFolders/types";
import CreateFolderDialog from "src/views/assistant/projects/CreateFolderDialog";
import ListSkeleton from "src/views/assistant/projects/components/ListSkeleton";
import RetryableError from "src/views/assistant/projects/components/RetryableError";
import {
  PickerNode,
  isSelectable,
} from "src/views/assistant/projects/components/FolderPicker";
import {
  TreeNavProvider,
  useTreeKeyboardNav,
} from "src/views/assistant/projects/hooks/useTreeKeyboardNav";
import "src/features/library/dialogs/library-dialog.scss";

interface ProjectPickerDialogProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  /** Called with the picked folder, or with a project created from the dialog. */
  onPick: (folder: ProjectFolder) => void;
  /** Disables picking and closing while the caller acts on a pick. */
  busy?: boolean;
  /** Folder shown first, tagged "Current", and left out of the roots list. */
  pinnedFolderId?: number | null;
  /** The pinned folder itself; falls back to the matching root until it loads. */
  pinnedFolder?: ProjectFolder;
  /** Shown when the user has no folder they can add into. */
  emptyMessage: string;
  testIdPrefix: string;
  newProjectDataAttr?: string;
  /** Caller content rendered above the tree. */
  children?: React.ReactNode;
}

// A traversal-only folder has a deeper writable folder behind it.
const canTarget = (f: ProjectFolder) =>
  isSelectable(f) || !!f.is_traversal_only;

/** Destination picker over the user's projects, with "New project" always offered. */
const ProjectPickerDialog: React.FC<ProjectPickerDialogProps> = ({
  open,
  onClose,
  title,
  onPick,
  busy = false,
  pinnedFolderId = null,
  pinnedFolder,
  emptyMessage,
  testIdPrefix,
  newProjectDataAttr,
  children,
}) => {
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const {
    data: roots,
    isLoading: rootsLoading,
    isError: rootsError,
    refetch: refetchRoots,
  } = useFolders();

  const pinned =
    pinnedFolder ?? (roots ?? []).find((f) => f.id === pinnedFolderId) ?? null;
  const pinnedId = pinned?.id ?? pinnedFolderId;
  const otherRoots = (roots ?? []).filter((f) => f.id !== pinnedId);
  const hasAnyTarget =
    (pinned !== null && canTarget(pinned)) || otherRoots.some(canTarget);
  const treeNav = useTreeKeyboardNav(pinned?.id ?? otherRoots[0]?.id ?? null);

  return (
    <>
      <Dialog
        open={open}
        onClose={busy ? undefined : onClose}
        fullWidth
        maxWidth="sm"
        className="library-dialog"
        data-testid={`${testIdPrefix}-dialog`}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          {children}
          {rootsLoading ? (
            <ListSkeleton
              rows={4}
              height={28}
              testId={`${testIdPrefix}-loading`}
            />
          ) : rootsError ? (
            <RetryableError
              message="Couldn't load your projects."
              onRetry={() => void refetchRoots()}
              testId={`${testIdPrefix}-error`}
            />
          ) : hasAnyTarget ? (
            <TreeNavProvider
              value={{
                activeId: treeNav.activeId,
                setActiveId: treeNav.setActiveId,
              }}
            >
              <Box
                role="tree"
                aria-label="Select destination project"
                onKeyDown={treeNav.onKeyDown}
                sx={{ maxHeight: 360, overflow: "auto" }}
              >
                {pinned && (
                  <PickerNode
                    key={`current-${pinned.id}`}
                    folder={pinned}
                    level={0}
                    busy={busy}
                    onPick={onPick}
                    isCurrent
                  />
                )}
                {otherRoots.map((folder) => (
                  <PickerNode
                    key={folder.id}
                    folder={folder}
                    level={0}
                    busy={busy}
                    onPick={onPick}
                  />
                ))}
              </Box>
            </TreeNavProvider>
          ) : (
            <Alert severity="info">{emptyMessage}</Alert>
          )}
          {busy && (
            <Box
              sx={{ display: "flex", justifyContent: "center", py: 1 }}
              data-testid={`${testIdPrefix}-busy`}
            >
              <CircularProgress size={20} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateProjectOpen(true)}
            disabled={busy}
            data-testid={`${testIdPrefix}-new-project`}
            data-attr={newProjectDataAttr}
          >
            New project
          </Button>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <CreateFolderDialog
        open={createProjectOpen}
        onClose={() => setCreateProjectOpen(false)}
        // A project minted here is the intended destination: pick it directly.
        onCreated={onPick}
      />
    </>
  );
};

export default ProjectPickerDialog;
